import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, CheckCircle2 } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [videoStarted, setVideoStarted] = useState(false);

  const handleStartQuiz = () => {
    navigate('/quiz');
  };

  const handleVideoPlay = () => {
    setVideoStarted(true);
    if (window.fbq) {
      window.fbq('trackCustom', 'VideoPlay', {
        content_name: 'Landing VSL',
        page: 'LandingPage'
      });
    }
  };

  const bullets = [
    { id: 'time', text: 'Resultado inmediato · 3 minutos' },
    { id: 'framework', text: 'Basado en el framework que usamos con +50 agencias y consultorías' },
    { id: 'free', text: 'Sin compromiso. Sin tarjeta. Solo claridad.' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Logo Header */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-50">
        <img 
          src="https://customer-assets.emergentagent.com/job_sales-audit-4/artifacts/laaotby8_v1_white.png" 
          alt="Sales Boost"
          className="h-10 md:h-12 lg:h-16 w-auto opacity-90"
          data-testid="sales-boost-logo"
        />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-24">
        {/* Disclaimer Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 sm:mb-8 mt-16 sm:mt-16"
        >
          <div className="bg-gradient-to-r from-[#802ef8]/10 to-[#7df9c5]/10 border border-[#802ef8]/30 rounded-md p-3 sm:p-4 text-center">
            <p className="text-xs sm:text-sm md:text-base text-[#a3a3a3]">
              <span className="text-white font-semibold">Este diagnóstico es exclusivo para:</span> Agencias de Marketing, Consultorías y Empresas B2B
            </p>
          </div>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 tracking-tighter px-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="landing-headline"
          >
            Oferta, marketing o ventas — uno de los tres está roto. Descubre cuál en 3 minutos.
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-[#a3a3a3] max-w-3xl mx-auto px-2" data-testid="landing-subheadline">
            Este diagnóstico rápido te muestra exactamente dónde se rompe tu proceso de ventas — y cuánto te está costando.
          </p>
        </motion.div>

        {/* Video Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 sm:mb-12"
        >
          <div 
            className="relative border border-[#262626] rounded-md overflow-hidden bg-[#141414] mx-auto"
            style={{ maxWidth: '400px' }}
            data-testid="video-container"
          >
            {!videoStarted ? (
              <div style={{ padding: '177.78% 0 0 0', position: 'relative' }}>
                <img 
                  src="https://images.unsplash.com/photo-1770745560263-a8fc696de90b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZXh0dXJlfGVufDB8fHx8MTc3NTUxMjEzM3ww&ixlib=rb-4.1.0&q=85"
                  alt="Video cover"
                  className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <button
                    onClick={handleVideoPlay}
                    className="bg-[#802ef8] hover:bg-[#9b5aff] w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10 animate-pulse"
                    data-testid="video-play-button"
                  >
                    <Play className="w-8 h-8 sm:w-10 sm:h-10 ml-1" fill="white" />
                  </button>
                  <span className="text-sm sm:text-base text-white font-medium bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full z-10">
                    Pulsa para ver el video
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ padding: '177.78% 0 0 0', position: 'relative' }}>
                <iframe
                  src="https://player.vimeo.com/video/1180942543?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  title="Sales Boost VSL"
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mb-8 sm:mb-12"
        >
          <button
            onClick={handleStartQuiz}
            className="px-6 sm:px-8 py-3 sm:py-4 rounded-md text-base sm:text-lg font-semibold transition-all w-full sm:w-auto bg-[#802ef8] hover:bg-[#9b5aff] text-white hover:scale-105 shadow-lg shadow-[#802ef8]/20"
            data-testid="start-quiz-button"
          >
            <span className="flex items-center justify-center gap-2">
              Hacer mi diagnóstico gratuito →
            </span>
          </button>
        </motion.div>

        {/* Bullets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="space-y-3 sm:space-y-4 max-w-2xl mx-auto"
        >
          {bullets.map((bullet) => (
            <div 
              key={bullet.id} 
              className="flex items-start gap-2 sm:gap-3 text-[#a3a3a3]"
              data-testid={`landing-bullet-${bullet.id}`}
            >
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#7df9c5] flex-shrink-0 mt-0.5" />
              <span className="text-sm sm:text-base md:text-lg">{bullet.text}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default LandingPage;